// The browser's copy of the COD form rules: a courtesy pre-check, never the authority.
import { normalizePhoneNumber, visibleCodFields, type CodFieldValue, type CodFormConfig, type CodRenderField } from '@xshop/shared';
import { DEFAULT_CHECKOUT_LABELS, type CheckoutLabels } from './checkoutLabels';

// The shortest answer the API accepts for each built-in field, mirrored so a shopper is told early.
const MIN_LENGTHS: Record<string, number> = Object.assign(Object.create(null), {
  fullName: 2, firstName: 2, lastName: 2, phone: 6, address1: 3
});

// Reads one answer as a trimmed string, so a blank-but-present value still counts as missing.
const asText = (value: CodFieldValue | undefined): string =>
  value === undefined || value === null ? '' : String(value).trim();

// Fills one message template with the field's label and, where it has one, the shortest answer it takes.
const say = (template: string, field: CodRenderField, min = 0): string =>
  template.replace('{field}', field.label).replace('{min}', String(min));

// Scores one field, returning the message to show under it, in the card's language, or null when it passes.
function checkField(field: CodRenderField, value: CodFieldValue | undefined, countryCode: string, words: CheckoutLabels): string | null {
  if (field.type === 'CHECKBOX') return field.required && value !== true ? say(words.fieldRequired, field) : null;
  const text = asText(value);
  if (text === '') return field.required ? say(words.fieldRequired, field) : null;
  // A phone number is judged by its own rule, whose message shows the shape it takes, before any length rule.
  if (field.key === 'phone') return normalizePhoneNumber(text, countryCode).isValid ? null : say(words.fieldPhone, field);
  const min = MIN_LENGTHS[field.key];
  if (min !== undefined && text.length < min) return say(words.fieldTooShort, field, min);
  if (field.type === 'SELECT' && !field.options.some((option) => option.value === text)) return say(words.fieldChoose, field);
  return null;
}

// Validates the whole form against the store's configuration, keyed by field so each input can show its own error.
export function validateCodValues(
  config: CodFormConfig,
  values: Record<string, CodFieldValue>,
  countryCode: string,
  words: CheckoutLabels = DEFAULT_CHECKOUT_LABELS
): Record<string, string> {
  const errors: Record<string, string> = Object.create(null);
  for (const field of visibleCodFields(config)) {
    const message = checkField(field, values[field.key], countryCode, words);
    if (message) errors[field.key] = message;
  }
  return errors;
}

// Reads the per-field messages the API returns when its own check refuses the submission.
export function serverFieldErrors(error: unknown): Record<string, string> {
  const details = (error as { details?: { fields?: Record<string, string> } })?.details;
  return details?.fields ?? {};
}
